import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import Typography from '@mui/material/Typography';
import Helmet from 'components/Helmet';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import config from 'config';
import { getLocalizedPath } from 'helpers/getLocale';
import CreepyButton from 'components/CreepyButton';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  maxWidth: 1030,
  margin: '0 auto',
  gap: 60,
  padding: '50px 0',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
    padding: '0',
    gap: 0
  }
}));

const LeftSidebar = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})(({ theme, isVisible }) => ({
  width: '50%',
  animation: isVisible ? `${fadeIn} 0.6s ease-out 0.1s both` : 'none',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  }
}));

const RightSidebar = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})(({ theme, isVisible }) => ({
  width: '50%',
  height: 325,
  willChange: 'transform, opacity',
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${slideInRight} 0.8s ease-out 0.4s both` : 'none',
  display: 'flex',
  justifyContent: 'flex-end',
  paddingRight: '20px',
  // Use aspect-ratio (not explicit height) so the displayed box matches
  // the loaded image's natural intrinsic ratio at every breakpoint.
  // Lighthouse `image-aspect-ratio` flagged the previous hardcoded heights
  // because 285/325 (desktop), 225/255 (md) and 160/180 (sm) all drift from
  // the served image's 284/325 ≈ 0.874 by ~1-2%. A single aspect-ratio
  // declaration keeps every breakpoint consistent with the source.
  '& img': {
    width: 285,
    height: 'auto',
    aspectRatio: '285 / 325',
    opacity: isVisible ? 1 : 0,
    animation: isVisible ? `${scaleIn} 0.8s ease-out 0.6s both` : 'none'
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'center',
    height: 289,
    paddingRight: 0,
    '& img': {
      width: 225,
      height: 'auto'
    }
  },
  [theme.breakpoints.down('sm')]: {
    alignSelf: 'center',
    height: 200,
    '& img': {
      width: 160,
      height: 'auto',
      marginBottom: 30
    }
  }
}));

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})(({ theme, isVisible }) => ({
  marginBottom: 30,
  fontWeight: 600,
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInUp} 0.8s ease-out 0.3s both` : 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    textAlign: 'center'
  }
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 20,
  marginTop: 30,
  maxWidth: 300,
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    gap: 15
  }
}));

const CreepyButtonRounded = styled(CreepyButton, {
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'delay'
})(({ theme, isVisible, delay }) => ({
  borderRadius: 25,
  '--creepy-btn-font-size': '16px',
  '--creepy-btn-line-height': '19px',
  '--creepy-btn-font-weight': 500,
  '--creepy-btn-pad-y': '11px',
  '--creepy-btn-pad-left': '18px',
  '--creepy-btn-pad-right': '18px',
  '--creepy-btn-pad-right-hover': '48px',

  transition: 'all 0.3s ease',
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInUp} 0.6s ease-out ${delay}s both` : 'none',
  width: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  },
  [theme.breakpoints.down('sm')]: {
    '--creepy-btn-pad-right-hover': '44px'
  }
}));

const ButtonRounded = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'delay'
})(({ theme, isVisible, delay }) => ({
  borderRadius: 25,
  // Color inherited from theme MuiButton root + contained/outlined-primary
  // variants (rgba(34, 34, 34, 1)). Was theme.palette.black.main = #000.
  fontSize: 16,
  padding: '11px 18px',
  transition: 'all 0.3s ease',
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInUp} 0.6s ease-out ${delay}s both` : 'none',
  width: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  }
}));

const ButtonText = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'delay' && prop !== 'size'
})(({ theme, isVisible, delay }) => ({
  color: theme.palette.black.main,
  textAlign: 'center',
  cursor: 'pointer',
  position: 'relative',
  marginTop: 5,
  display: 'inline-flex',
  justifyContent: 'center',
  width: '100%',
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInUp} 0.6s ease-out ${delay}s both` : 'none',
  '& p': {
    display: 'inline-block',
    position: 'relative',
    zIndex: 2,
    '& > span': {
      position: 'relative',
      borderBottom: `1px solid ${theme.palette.black.main}`,
      fontSize: 16,
      lineHeight: '19px',
      '&::before': {
        content: '""',
        position: 'absolute',
        // Drop the salad halo so it sits across the underline / descender
        // instead of the x-height. Was 8 (band looked centred on the text
        // — user wanted it pushed "a little down").
        top: 14,
        left: 0,
        right: 0,
        height: 0,
        zIndex: -1,
        WebkitBoxShadow: '0px 0px 11px 3px rgba(185,248,51,1)',
        MozBoxShadow: '0px 0px 11px 3px rgba(185,248,51,1)',
        boxShadow: '0px 0px 11px 3px rgba(185,248,51,1)'
      }
    }
  },
  '& > span': {
    display: 'none'
  }
}));

const ShowSM = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block'
  }
}));

const HideSM = styled('div')(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TypographyStyled = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isVisible'
})(({ theme, isVisible }) => ({
  opacity: isVisible ? 1 : 0,
  animation: isVisible ? `${fadeInUp} 0.8s ease-out 0.8s both` : 'none',
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    lineHeight: '20px'
  }
}));

const Start = ({ handleOpen }) => {
  const navigate = useNavigate();
  const t = useTranslate('Onboarding');
  const [isVisible, setIsVisible] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Trigger animations on mount
    setIsVisible(true);
  }, []);

  const handleRedirectProfessions = React.useCallback(() => {
    navigate(getLocalizedPath('/professions'));
  }, [navigate]);

  return (
    <>
      <Helmet />
      <Wrapper>
        <LeftSidebar isVisible={isVisible}>
          <HideSM>
            <Title component="h2" variant={'h2'} isVisible={isVisible}>{t('IntroTitle')}</Title>
          </HideSM>

          <TypographyStyled isVisible={isVisible}>{t('IntroText')}</TypographyStyled>

          <Actions>
            {isMobile ? (
              <ButtonRounded
                size="small"
                variant={'contained'}
                color={'primary'}
                onClick={handleOpen}
                isVisible={isVisible}
                delay={1.3}
                aria-label={t('WantToChose')}>
                {t('WantToChose')}
              </ButtonRounded>
            ) : (
              <CreepyButtonRounded
                onClick={handleOpen}
                isVisible={isVisible}
                delay={1.3}
                aria-label={t('WantToChose')}>
                {t('WantToChose')}
              </CreepyButtonRounded>
            )}

            <ButtonRounded 
              size="small" 
              variant={'outlined'} 
              onClick={handleRedirectProfessions}
              isVisible={isVisible}
              delay={1.6}
              aria-label={t('KnowProfession')}>
              {t('KnowProfession')}
            </ButtonRounded>

            <ButtonText 
              size="small" 
              onClick={handleRedirectProfessions}
              isVisible={isVisible}
              delay="3">
              <Typography>
                <span>{t('HaveAccount')}</span>
              </Typography>
            </ButtonText>
          </Actions>
        </LeftSidebar>
        <RightSidebar isVisible={isVisible}>
          <img
            src={`${config.imageSource}/9cbb1a99-45f2-4677-259d-f03e92932f00/w285h325`}
            alt="Troll Illustration"
            priority="high"
            fetchPriority="high"
            width={285}
            height={325}
            loading="eager"
          />
        </RightSidebar>

        <ShowSM>
          <Title component="h2" variant={'h2'} isVisible={isVisible}>{t('IntroTitle')}</Title>
        </ShowSM>
      </Wrapper>
    </>
  );
};

export default Start;
